/* eslint-disable no-nested-ternary */
/* eslint-disable no-underscore-dangle */
import { useDispatch } from "react-redux";
import { useEffect } from "react";
import PropTypes from "prop-types";

import { headline2, headline3 } from "@shared/styles";
import Toggle from "@shared/v2/Toggle";
import { Info } from "@shared/v2/Icomoon";
import Tooltip from "@shared/v2/Tooltip";
import { tryCatchHandlr } from "@shared/Utilities";
import { caseTransformingAxios } from "@shared/v2/caseTransformingAxios";
import TextInput from "@shared/v2/TextInput";
import Title from "../ModalSections/Title";
import DefaultLinks from "../ModalSections/DefaultLinks";
import CustomLinks from "../ModalSections/CustomLinks";

import { containerView, borderHr } from "./styles";
import AboutMe from "../ModalSections/AboutMe";
import PhoneNumberDropdown from "./PhoneNumberDropdown";
import { useIsAdmin } from "../../reducers/layoutReducer/selectors";
import { useContactPerson } from "../../reducers/contactsReducer/selectors";
import { addCustomLink, setCustomLink, setPersonValue } from "../../reducers/contactsReducer";
import LicenseNumber from "../ModalSections/LicenseNumber";

// Helper functions for website display phone details
const getWebsiteDisplayPhone = (person, category) => {
  const websitePhones = person.websiteDisplayPhoneDetailsAttributes || [];
  const phone = websitePhones.find((p) => p.category === category && !p._destroy);

  if (!phone) return null;

  return {
    phoneNumber: phone.value,
    default: phone.position === 1,
    id: phone.id || phone.tempId,
  };
};

// Normalize phone number by removing non-digit characters
const normalizePhoneNumber = (phoneNumber) => {
  return (phoneNumber || "").replace(/\D/g, "");
};

const getWebsiteDisplayPhoneValue = (person, category) => {
  const phone = getWebsiteDisplayPhone(person, category);
  return phone ? phone.phoneNumber : null;
};

const isDefaultWebsiteDisplayPhone = (person, category) => {
  const phone = getWebsiteDisplayPhone(person, category);
  return phone ? phone.default : false;
};

const WebsiteInfoViewRef = ({ modalType }) => {
  const isAdmin = useIsAdmin();
  const dispatch = useDispatch();
  const person = useContactPerson();

  const { subdomain } = person.loginUserAttributes || {};
  const websiteLink = person.customLinksAttributes?.find((link) => link.name === "website");

  useEffect(() => {
    if (!person?.loginUserAttributes?.id) return;

    (async () => {
      const [res] = await tryCatchHandlr(
        caseTransformingAxios.get(`/account/${person?.loginUserAttributes?.id}/idx_configuration.json`, {
          maxRedirects: 0,
        }),
      );

      dispatch(
        setPersonValue({
          key: "domain",
          value: res.data?.data?.domain,
        }),
      );
    })();
  }, [person?.loginUserAttributes?.id]);

  return (
    <div className={containerView}>
      <div className={`tw-text-neutral-gray-75 ${headline2}`}>Website Info</div>
      <div className={containerView}>
        <div className={headline3}>Basic</div>
        {(["user", "websiteProfile"].includes(modalType) || person.websiteRosterPersonAgent === "true") && (
          <div className="tw-flex tw-gap-24px">
            <Toggle
              id="webroster_toggle"
              checked={String(person.showOnWebsiteRoster) === "true"}
              onChange={() =>
                dispatch(
                  setPersonValue({
                    key: "showOnWebsiteRoster",
                    value: String(person.showOnWebsiteRoster) === "true" ? "false" : "true",
                  }),
                )
              }
              text="Display on roster"
            />
            <Toggle
              id="webprofile_toggle"
              checked={String(person.showAgentProfilePage) === "true"}
              onChange={() =>
                dispatch(
                  setPersonValue({
                    key: "showAgentProfilePage",
                    value: String(person.showAgentProfilePage) === "true" ? "false" : "true",
                  }),
                )
              }
              text="Profile page"
            />
          </div>
        )}
        <Title />
        {modalType === "user" && <LicenseNumber />}
        {modalType === "websiteProfile" && person.websiteRosterPersonAgent === "true" && (
          <TextInput
            label="License #"
            value={person.websiteRosterPersonAgentLicenseId || ""}
            onChange={(e) =>
              dispatch(
                setPersonValue({
                  key: "websiteRosterPersonAgentLicenseId",
                  value: e.target.value,
                }),
              )
            }
          />
        )}
        <hr className={`${borderHr} tw-m-0`} />
        <div className={headline3}>Contact Information</div>
        <div className="tw-flex tw-flex-col tw-gap-[8px]">
          <div className="tw-flex tw-text-14d tw-font-semibold tw-text-gray-50">
            <div className="tw-mr-auto tw-w-[352px] tw-flex tw-items-center tw-gap-[4px]">
              <span>Phone Number</span>
              <Tooltip
                content={
                  <div className="tw-w-[180px] tw-text-left">
                    Phone numbers selected here are displayed on your Brivity website and subdomain. Removing
                    a number here does not remove it from your CRM account.
                  </div>
                }
                trigger={<Info size="m" className="tw-text-gray-75" />}
                placement="top"
              />
            </div>
            <div className="tw-mr-[24px] tw-w-[86px]">Displayed As</div>
            <div className="tw-flex tw-gap-[4px] tw-w-[66px]">
              <div className="tw-text-14d tw-font-semibold">Default</div>
              <Tooltip
                content={
                  <div className="tw-w-[180px] tw-text-left">
                    Your main phone number. Shown in areas of your website where only one number is set to
                    display such as the property details page.
                  </div>
                }
                trigger={<Info size="m" className="tw-text-gray-75" />}
                placement="top"
              />
            </div>
          </div>
          <PhoneNumberDropdown
            phoneNumberObj={getWebsiteDisplayPhone(person, "mobile")}
            checked={isDefaultWebsiteDisplayPhone(person, "mobile")}
            displayedAs="Cell"
            onCheck={() => {
              // Set mobile as default and office as non-default
              const websitePhones = person.websiteDisplayPhoneDetailsAttributes || [];
              const updatedPhones = websitePhones.map((phone) => ({
                ...phone,
                position: phone.category === "mobile" ? 1 : 2,
              }));

              dispatch(
                setPersonValue({
                  key: "websiteDisplayPhoneDetailsAttributes",
                  value: updatedPhones,
                }),
              );
            }}
            onTrash={() => {
              // Remove mobile phone and set office as default if it exists
              const websitePhones = person.websiteDisplayPhoneDetailsAttributes || [];
              const updatedPhones = websitePhones.map((phone) => {
                if (phone.category === "office") return { ...phone, position: 1 };

                return { ...phone, _destroy: true };
              });

              dispatch(
                setPersonValue({
                  key: "websiteDisplayPhoneDetailsAttributes",
                  value: updatedPhones,
                }),
              );
            }}
            onChange={(p) => {
              if (!p) {
                // Remove mobile phone
                const websitePhones = person.websiteDisplayPhoneDetailsAttributes || [];
                const updatedPhones = websitePhones.filter((phone) => phone.category !== "mobile");

                dispatch(
                  setPersonValue({
                    key: "websiteDisplayPhoneDetailsAttributes",
                    value: updatedPhones,
                  }),
                );
                return;
              }

              // Add or update mobile phone
              const websitePhones = person.websiteDisplayPhoneDetailsAttributes || [];
              const mobilePhone = websitePhones.find((phone) => phone.category === "mobile");
              const officePhone = websitePhones.find((phone) => phone.category === "office");

              if (mobilePhone) {
                // Update existing mobile phone
                const updatedPhones = websitePhones.map((phone) => {
                  if (phone.category === "mobile") {
                    return { ...phone, value: p.value, _destroy: false };
                  }
                  return phone;
                });

                dispatch(
                  setPersonValue({
                    key: "websiteDisplayPhoneDetailsAttributes",
                    value: updatedPhones,
                  }),
                );
              } else {
                // Add new mobile phone
                const position = !officePhone ? 1 : isDefaultWebsiteDisplayPhone(person, "office") ? 2 : 1;

                if (position === 1 && officePhone) {
                  // Update office phone position if mobile is default
                  const updatedPhones = [
                    ...websitePhones.map((phone) => {
                      if (phone.category === "office") {
                        return { ...phone, position: 2 };
                      }
                      return phone;
                    }),
                    {
                      name: "website_display_phone",
                      category: "mobile",
                      value: p.value,
                      position: 1,
                      tempId: Date.now(),
                    },
                  ];

                  dispatch(
                    setPersonValue({
                      key: "websiteDisplayPhoneDetailsAttributes",
                      value: updatedPhones,
                    }),
                  );
                } else {
                  // Just add the new mobile phone
                  dispatch(
                    setPersonValue({
                      key: "websiteDisplayPhoneDetailsAttributes",
                      value: [
                        ...websitePhones,
                        {
                          name: "website_display_phone",
                          category: "mobile",
                          value: p.value,
                          position,
                          tempId: Date.now(),
                        },
                      ],
                    }),
                  );
                }
              }
            }}
            disabledNumber={getWebsiteDisplayPhoneValue(person, "office")}
            key={`mobile-${normalizePhoneNumber(getWebsiteDisplayPhoneValue(person, "mobile"))}`}
          />
          <PhoneNumberDropdown
            phoneNumberObj={getWebsiteDisplayPhone(person, "office")}
            checked={isDefaultWebsiteDisplayPhone(person, "office")}
            displayedAs="Office"
            onCheck={() => {
              // Set office as default and mobile as non-default
              const websitePhones = person.websiteDisplayPhoneDetailsAttributes || [];
              const updatedPhones = websitePhones.map((phone) => ({
                ...phone,
                position: phone.category === "office" ? 1 : 2,
              }));

              dispatch(
                setPersonValue({
                  key: "websiteDisplayPhoneDetailsAttributes",
                  value: updatedPhones,
                }),
              );
            }}
            onTrash={() => {
              // Remove office phone and set mobile as default if it exists
              const websitePhones = person.websiteDisplayPhoneDetailsAttributes || [];

              const updatedPhones = websitePhones.map((phone) => {
                if (phone.category === "mobile") return { ...phone, position: 1 };
                return { ...phone, _destroy: true };
              });

              dispatch(
                setPersonValue({
                  key: "websiteDisplayPhoneDetailsAttributes",
                  value: updatedPhones,
                }),
              );
            }}
            onChange={(p) => {
              if (!p) {
                // Remove office phone
                const websitePhones = person.websiteDisplayPhoneDetailsAttributes || [];
                const updatedPhones = websitePhones.filter((phone) => phone.category !== "office");

                dispatch(
                  setPersonValue({
                    key: "websiteDisplayPhoneDetailsAttributes",
                    value: updatedPhones,
                  }),
                );
                return;
              }

              // Add or update office phone
              const websitePhones = person.websiteDisplayPhoneDetailsAttributes || [];
              const officePhone = websitePhones.find((phone) => phone.category === "office");
              const mobilePhone = websitePhones.find((phone) => phone.category === "mobile");

              if (officePhone) {
                // Update existing office phone
                const updatedPhones = websitePhones.map((phone) => {
                  if (phone.category === "office") {
                    return { ...phone, value: p.value, _destroy: false };
                  }
                  return phone;
                });

                dispatch(
                  setPersonValue({
                    key: "websiteDisplayPhoneDetailsAttributes",
                    value: updatedPhones,
                  }),
                );
              } else {
                // Add new office phone
                const position = !mobilePhone ? 1 : isDefaultWebsiteDisplayPhone(person, "mobile") ? 2 : 1;

                if (position === 1 && mobilePhone) {
                  // Update mobile phone position if office is default
                  const updatedPhones = [
                    ...websitePhones.map((phone) => {
                      if (phone.category === "mobile") {
                        return { ...phone, position: 2, _destroy: false };
                      }
                      return phone;
                    }),
                    {
                      name: "website_display_phone",
                      category: "office",
                      value: p.value,
                      position: 1,
                      tempId: Date.now(),
                      _destroy: false,
                    },
                  ];

                  dispatch(
                    setPersonValue({
                      key: "websiteDisplayPhoneDetailsAttributes",
                      value: updatedPhones,
                    }),
                  );
                } else {
                  // Just add the new office phone
                  dispatch(
                    setPersonValue({
                      key: "websiteDisplayPhoneDetailsAttributes",
                      value: [
                        ...websitePhones,
                        {
                          name: "website_display_phone",
                          category: "office",
                          value: p.value,
                          position,
                          tempId: Date.now(),
                          _destroy: false,
                        },
                      ],
                    }),
                  );
                }
              }
            }}
            disabledNumber={getWebsiteDisplayPhoneValue(person, "mobile")}
            key={`office-${normalizePhoneNumber(getWebsiteDisplayPhoneValue(person, "office"))}`}
          />
        </div>
        {/* NOTE: this will be reintroduced later */}
        {/*
          <hr className={`${borderHr} tw-m-0`} />
          <div className={headline3}>Subdomain</div>
          <div className="tw-text-gray-50 tw-text-14d">
            <span className={isAdmin ? "tw-hidden" : ""}>
              Your subdomain will be identical to the account owner’s website except their personal
              information is replaced with yours (where applicable).
            </span>{" "}
            Leads generated on your subdomain will be automatically routed to you. Limit: 1 per Brivity user.
          </div>
          {(!subdomain || subdomain?.trim().length === 0) && (
            <>
              <div className="">You do not currently have a subdomain.</div>
              <Button
                schema="secondary"
                className="tw-w-[98px] tw-flex tw-items-center tw-whitespace-nowrap"
                onClick={onSubdomainOpen}
              >
                <PlusSolidV6 size="s" className="tw-mr-[6px]" />
                <span>SUBDOMAIN</span>
              </Button>
            </>
          )}
          {websiteURL && (
            <div className="tw-p-8px tw-flex tw-justify-between tw-border-1px tw-border-solid tw-border-gray-100 tw-border-gray-10 tw-rounded-4px">
              <a
                className="tw-text-theme-text-button-color tw-text-14d tw-flex tw-items-center tw-justify-center tw-gap-6px"
                href={`http://www.${websiteURL}`}
                target="_blank"
                rel="noreferrer"
              >
                <span>{websiteURL}</span>
                <ArrowUpRightFromSquareSolidV6 size="m" />
              </a>
              <div className="tw-flex tw-gap-8px">
                {showCopy && (
                  <div className="tw-flex tw-gap-4px tw-items-center tw-text-green-100">
                    <CheckSolidV6 size="m" />
                    <span>Copied!</span>
                  </div>
                )}
                <Button
                  className="!tw-w-24px tw-flex tw-items-center tw-justify-center"
                  size="small"
                  schema="tertiary"
                  onClick={() => {
                    navigator.clipboard.writeText(websiteURL);
                    setShowCopy(true);
                    setTimeout(() => {
                      setShowCopy(false);
                    }, 2000);
                  }}
                >
                  <CopySolidV6 size="m" />
                </Button>
                <Button
                  className="!tw-w-24px tw-flex tw-items-center tw-justify-center"
                  size="small"
                  schema="tertiary"
                  onClick={onSubdomainEdit}
                >
                  <PencilSolidV6 size="m" />
                </Button>
                <Button
                  className="!tw-w-24px tw-flex tw-items-center tw-justify-center"
                  size="small"
                  schema="tertiary"
                  onClick={onSubdomainRemove}
                >
                  <TrashCanSolidV6 size="m" />
                </Button>
              </div>
            </div>
          )}
         */}
        {(["user", "lender", "websiteProfile"].includes(modalType) ||
          person.websiteRosterPreferredLender === "true" ||
          person.websiteRosterPersonAgent === "true") && (
          <TextInput
            label="Website Link"
            placeholder={
              modalType === "lender" || person.websiteRosterPreferredLender === "true"
                ? "Provide a link to this lender's website or pre-qualification application"
                : "Provide a link to this agent's website"
            }
            value={websiteLink?.value || ""}
            onChange={(e) => {
              const websiteLinkValue = { position: 1000, value: e.target.value, name: "website" };
              if (websiteLink) dispatch(setCustomLink(websiteLinkValue));
              else dispatch(addCustomLink(websiteLinkValue));
            }}
          />
        )}
        {["user"].includes(modalType) && (isAdmin || subdomain) && (
          <>
            <hr className={`${borderHr} tw-m-0`} />
            <div className={headline3}>Review Links</div>
            <div className="tw-text-gray-50">
              Enter the links for the websites you use to collect client reviews. They are added to your{" "}
              <b>Links</b> page. (Example: john.mywebsite.com/links)
            </div>
            <DefaultLinks />
            <hr className={`${borderHr} tw-m-0`} />
            <div className={headline3}>Custom Links</div>
            <div className="tw-text-gray-50 tw-tracking-[-0.4px]">
              Custom links are added to your <b>Links</b> page. (Example: john.mywebsite.com/links)
            </div>
            <CustomLinks />
          </>
        )}
        {(["user", "websiteProfile"].includes(modalType) || person.websiteRosterPersonAgent === "true") && (
          <>
            <hr className={`${borderHr} tw-m-0`} />
            <div className={headline3}>About Me</div>
            <AboutMe />
          </>
        )}
      </div>
    </div>
  );
};

WebsiteInfoViewRef.propTypes = {
  modalType: PropTypes.oneOf(["user", "websiteProfile", "lender"]).isRequired,
};

export default WebsiteInfoViewRef;
